import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, RefreshCw, Trash2, GripVertical, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";

export interface OutlineModule {
  title: string;
  lessons: string[];
}

export interface CourseOutline {
  title: string;
  description: string;
  modules: OutlineModule[];
}

interface OutlineStepProps {
  requestId: string | null;
  outline: CourseOutline | null;
  onOutlineReady: (outline: CourseOutline) => void;
  onRegenerate: () => void;
  isGenerating: boolean;
}

export const OutlineStep: React.FC<OutlineStepProps> = ({
  requestId,
  outline,
  onOutlineReady,
  onRegenerate,
  isGenerating,
}) => {
  const [editableOutline, setEditableOutline] = useState<CourseOutline | null>(outline);
  const [openModuleIdx, setOpenModuleIdx] = useState<number | null>(0);

  // Subscribe to realtime updates for outline generation
  useEffect(() => {
    if (!requestId || outline) return;

    const channel = supabase
      .channel(`outline-${requestId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "course_generation_requests",
          filter: `id=eq.${requestId}`,
        },
        (payload) => {
          const updated = payload.new as any;
          if (updated.status === "outline_ready" && updated.outline) {
            const parsed = updated.outline as CourseOutline;
            setEditableOutline(parsed);
            onOutlineReady(parsed);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [requestId, outline, onOutlineReady]);

  useEffect(() => {
    setEditableOutline(outline);
  }, [outline]);

  const updateModuleTitle = (moduleIdx: number, title: string) => {
    if (!editableOutline) return;
    const updated = { ...editableOutline };
    updated.modules = [...updated.modules];
    updated.modules[moduleIdx] = { ...updated.modules[moduleIdx], title };
    setEditableOutline(updated);
    onOutlineReady(updated);
  };

  const updateLessonTitle = (moduleIdx: number, lessonIdx: number, title: string) => {
    if (!editableOutline) return;
    const updated = { ...editableOutline };
    updated.modules = [...updated.modules];
    updated.modules[moduleIdx] = {
      ...updated.modules[moduleIdx],
      lessons: [...updated.modules[moduleIdx].lessons],
    };
    updated.modules[moduleIdx].lessons[lessonIdx] = title;
    setEditableOutline(updated);
    onOutlineReady(updated);
  };

  const removeLesson = (moduleIdx: number, lessonIdx: number) => {
    if (!editableOutline) return;
    const updated = { ...editableOutline };
    updated.modules = [...updated.modules];
    updated.modules[moduleIdx] = {
      ...updated.modules[moduleIdx],
      lessons: updated.modules[moduleIdx].lessons.filter((_, i) => i !== lessonIdx),
    };
    // Remove empty modules
    if (updated.modules[moduleIdx].lessons.length === 0) {
      updated.modules = updated.modules.filter((_, i) => i !== moduleIdx);
    }
    setEditableOutline(updated);
    onOutlineReady(updated);
  };

  if (isGenerating && !editableOutline) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
        <p className="text-sm text-muted-foreground">Generating course outline...</p>
        <p className="text-xs text-muted-foreground/70">This usually takes 10-20 seconds</p>
      </div>
    );
  }

  if (!editableOutline) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-12">
        <p className="text-sm text-muted-foreground">No outline generated yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="border-b border-border pb-4">
        <div className="space-y-2 flex-1 min-w-0">
          <input
            type="text"
            value={editableOutline.title}
            placeholder="Course title"
            onChange={(e) => {
              const updated = { ...editableOutline, title: e.target.value };
              setEditableOutline(updated);
              onOutlineReady(updated);
            }}
            className="text-xl font-semibold bg-transparent outline-none w-full text-foreground placeholder:text-muted-foreground/50"
          />
          <input
            type="text"
            value={editableOutline.description}
            placeholder="Course description"
            onChange={(e) => {
              const updated = { ...editableOutline, description: e.target.value };
              setEditableOutline(updated);
              onOutlineReady(updated);
            }}
            className="text-sm text-muted-foreground bg-transparent outline-none w-full placeholder:text-muted-foreground/40"
          />
          <p className="text-xs text-muted-foreground/60">
            {editableOutline.modules.length} modules · structured by Shattara AI
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-4 max-h-[420px] overflow-y-auto pr-2">
        {editableOutline.modules.map((module, moduleIdx) => (
          <div
            key={moduleIdx}
            className="rounded-2xl border border-border bg-card transition-all duration-200 hover:border-foreground/20 hover:shadow-sm overflow-hidden"
          >
            <div
              className="flex items-center gap-3 px-4 py-3 cursor-pointer select-none"
              onClick={() => setOpenModuleIdx(openModuleIdx === moduleIdx ? null : moduleIdx)}
            >
              <span className="text-xs font-medium text-muted-foreground w-16 shrink-0">
                Module {moduleIdx + 1}
              </span>
              <input
                type="text"
                value={module.title}
                onChange={(e) => updateModuleTitle(moduleIdx, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                className="flex-1 text-sm font-medium bg-transparent outline-none text-foreground"
              />
              <span className="text-xs text-muted-foreground shrink-0">
                {module.lessons.length} {module.lessons.length === 1 ? "lesson" : "lessons"}
              </span>
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 ${
                  openModuleIdx === moduleIdx ? "rotate-180" : ""
                }`}
              />
            </div>
            {openModuleIdx === moduleIdx && (
              <div className="border-t border-border divide-y divide-border/50">
                {module.lessons.map((lesson, lessonIdx) => (
                  <div
                    key={lessonIdx}
                    className="flex items-center gap-2.5 px-4 py-2.5 group transition-colors duration-150 hover:bg-muted/40"
                  >
                    <GripVertical className="h-3.5 w-3.5 text-muted-foreground/40 shrink-0" />
                    <span className="text-xs text-muted-foreground/60 w-5 shrink-0 text-right">
                      {lessonIdx + 1}
                    </span>
                    <input
                      type="text"
                      value={lesson}
                      onChange={(e) => updateLessonTitle(moduleIdx, lessonIdx, e.target.value)}
                      className="flex-1 text-sm bg-transparent outline-none text-foreground"
                    />
                    <button
                      type="button"
                      onClick={() => removeLesson(moduleIdx, lessonIdx)}
                      className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-all shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
